import { useEffect, useMemo, useState, useRef, useContext } from 'react';

import debounce from 'lodash/debounce';

import { useBooleanState } from '@vyce/core/src/hooks';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import {
  postWorkersWorkedTimeOverviewRequest,
  getCompaniesWidgetsSharedData,
} from '@vyce/core/src/api/time';

import { widths, limits } from '../config';
import { transformData } from '../utils';
import { EnrichedWorkersWorkedTimeOverview, Props } from '../types';
import { NotificationContext } from '../../../../contexts/notificationContext';
import { useWidgetErrorHandler } from '@vyce/core/src/hooks/useWidgetErrorHandler';
import { generateExcelFileLink } from '../../../../utils';

export const useChartData = ({ selectedCompanyId, onForbidden }: Props) => {
  const { handleServerError } = useContext(NotificationContext);
  // Loading the widget: a 403 hides it instead of raising a toast.
  const handleWidgetError = useWidgetErrorHandler(onForbidden);
  const [fullData, setFullData] = useState<EnrichedWorkersWorkedTimeOverview[]>([]);
  const [page, setPage] = useState(0);
  const [downloadButtonLoading, setDownloadButtonLoadingTrue, setDownloadButtonLoadingFalse] =
    useBooleanState(false);
  const [maxPage, setMaxPage] = useState(0);
  const [limit, setLimit] = useState(limits.bigLimit);
  const ref = useRef<HTMLDivElement>(null);
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(true);
  const isMounted = useIsMounted();

  const cuttedData = useMemo(() => {
    if (page * limit > fullData.length) return fullData.slice(-limit);
    return fullData.slice((page - 1) * limit, page * limit);
  }, [fullData, page, limit]);

  const decreasePage = () => setPage(page - 1);
  const increasePage = () => setPage(page + 1);

  const fetchData = async () => {
    if (!selectedCompanyId) return;
    setLoadingTrue();

    try {
      const { data } = await postWorkersWorkedTimeOverviewRequest({
        companyId: selectedCompanyId,
        payload: { approved: true },
      });
      const enrichedData = transformData(data.items);
      if (!isMounted.current) return;
      setFullData(enrichedData.reverse());
      setLoadingFalse();
    } catch (e) {
      handleWidgetError(e);

      if (isMounted.current) setLoadingFalse();
    }
  };

  const getSharedFile = async () => {
    if (!selectedCompanyId) {
      return;
    }
    try {
      setDownloadButtonLoadingTrue();
      const res = await getCompaniesWidgetsSharedData({
        companyId: selectedCompanyId,
        target: 'workers_worked_time_overview',
        payload: { approved: true },
      });
      generateExcelFileLink(res);
    } catch (e) {
      handleServerError(e);
    } finally {
      if (isMounted.current) setDownloadButtonLoadingFalse();
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCompanyId]);

  useEffect(() => {
    const newMaxPage = fullData.length > limit ? Math.ceil(fullData.length / limit) : 0;
    setMaxPage(newMaxPage);
    setPage(newMaxPage);
  }, [limit, fullData]);

  useEffect(() => {
    const observeTarget = ref.current;
    if (!observeTarget) return;

    const debouncedCallback = debounce(entry => {
      const { width } = entry.contentRect;
      if (width >= widths.bigWidth) {
        setLimit(limits.bigLimit);
        return;
      }
      if (width >= widths.midWidth) {
        setLimit(limits.midLimit);
        return;
      }
      if (width >= widths.small) {
        setLimit(limits.smallLimit);
        return;
      }
      setLimit(limits.extraSmall);
    }, 300);

    const resizeObserver = new ResizeObserver(entries => {
      if (!Array.isArray(entries) || !entries.length) return;

      const entry = entries[0];
      debouncedCallback(entry);
    });

    resizeObserver.observe(observeTarget);

    return () => {
      debouncedCallback.cancel();
      resizeObserver.disconnect();
    };
  }, [ref]);

  return {
    loading,
    ref,
    cuttedData,
    showDecreaseButton: page > 1,
    showIncreaseButton: page !== maxPage,
    downloadButtonLoading,
    getSharedFile,
    increasePage,
    decreasePage,
  };
};
